import Image from '@space-core/components/image'
import LineClamp from '@space-core/components/lineClamp'
import { useMediaQuery } from '@space-core/hooks'
import { ScreenSizes } from '@space-core/styles'
import emptyAvatar from '@space-shell/assets/images/emptyAvatar.svg'
import { ProductPrice } from '@space-shell/common/components/productPrice'
import type { SectionType } from '@space-shell/common/services/sections'
import { type SectionProductDetail } from '@space-shell/common/services/sections'
import useGetProductDetails from '@space-shell/modules/home/hooks/useGetProductDetails/useGetProductDetails'
import useGetProductPrice from '@space-shell/modules/products/components/productCard/hooks/useGetProductPrice'

import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import SectionTitleContentLoader from './sectionTitleContentLoader'

import ButtonsCallToActions from '../buttonsCallToActions'
import { DefaultDynamicSectionsTitles } from '../section/constants'

const Wrapper = styled.div`
  max-width: 60rem;
`

const Avatar = styled(Image)`
  width: 2.5rem;
  height: 2.5rem;
`

const Separator = styled.hr`
  height: var(--spacer-5);
  width: 0.063rem;
`

export type SectionTitleProps = {
  sectionType: SectionType
  sectionTitle?: string
  product?: SectionProductDetail
  productId?: string
  sectionId?: string
  isPreview?: boolean
  isLoading?: boolean
  isDynamicSection?: boolean
}

const SectionTitle = ({
  productId,
  sectionId,
  product,
  sectionTitle,
  isPreview = false,
  isLoading,
  isDynamicSection,
  sectionType
}: SectionTitleProps) => {
  const {
    t,
    i18n: { language }
  } = useTranslation()

  const isMobile = useMediaQuery(`(max-width: ${ScreenSizes.sm})`)

  const {
    data: productPreview = {} as SectionProductDetail,
    isInitialLoading: isLoadingProductDetails
  } = useGetProductDetails({
    productId: productId || '',
    enabled: Boolean(productId) && isPreview
  })

  const sectionProduct = product || productPreview

  const { name, image, category, purchased } = sectionProduct
  const { checkoutPrice, price, showPrice } = useGetProductPrice({
    product: sectionProduct,
    language,
    purchased
  })

  if (isLoading || isLoadingProductDetails) {
    return <SectionTitleContentLoader />
  }

  const getDisplayTitle = () => {
    if (isDynamicSection && typeof sectionTitle === 'undefined') {
      return t(`sections.${DefaultDynamicSectionsTitles[sectionType]}.title`)
    }

    return sectionTitle
  }

  return (
    <Wrapper className='_px-4 _px-md-10 _mb-5'>
      <LineClamp lines={2}>
        <h2 className='_text-3 _text-gray-900 _m-0'>{getDisplayTitle()}</h2>
      </LineClamp>

      {Boolean(productId) && (
        <div className='_d-flex _align-items-center _mt-2'>
          <Avatar
            alt={name}
            aria-hidden
            objectFit='cover'
            className='_rounded'
            errorSrc={emptyAvatar}
            objectPosition='center'
            src={image?.url || emptyAvatar}
          />

          <div
            className={clsx('_d-flex _flex-column _justify-content-center', {
              '_mx-3': price || category
            })}>
            <LineClamp lines={2}>
              <h2 className='_text-2 _text-gray-900 _font-weight-light _m-0'>{name}</h2>
            </LineClamp>

            <div
              className={clsx('_d-flex', {
                '_flex-column ': isMobile,
                '_align-items-center': !isMobile
              })}>
              {category && (
                <p className='_text-1 _text-gray-600 _m-0'>{t(`products.${category}`)}</p>
              )}

              {category && showPrice && !isMobile && (
                <span className='_mx-1 _text-gray-600'>·</span>
              )}

              {showPrice && <ProductPrice checkoutPrice={checkoutPrice} product={sectionProduct} />}
            </div>
          </div>
          {!isMobile && (
            <>
              <Separator className='_border-0 _border-left _border-gray-200 _mx-5' />

              <ButtonsCallToActions
                product={sectionProduct}
                sectionId={sectionId}
                isPreview={isPreview}
              />
            </>
          )}
        </div>
      )}
    </Wrapper>
  )
}

export default SectionTitle
